import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Loader2, Search } from 'lucide-react';
import { ManageReservationCard } from '@/components/ManageReservationCard';
import { getGuestSession } from '@/lib/guestSession';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ManageReservation() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('code');
  const [form, setForm] = useState({ confirmation_code: '', room_number: '', last_name: '' });
  const [loading, setLoading] = useState(false);
  const [reservations, setReservations] = useState(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const lookup = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const body = { last_name: form.last_name };
      if (mode === 'code') body.confirmation_code = form.confirmation_code;
      else body.room_number = form.room_number;
      const res = await axios.post(`${API_URL}/reservations/manage-lookup`, body);
      setReservations(res.data.reservations);
    } catch (error) {
      setReservations(null);
      toast.error('Lookup failed', { description: error.response?.data?.detail || 'Please check your details' });
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (reservation) => {
    await axios.post(`${API_URL}/reservations/${reservation.id}/guest-cancel`, {
      booking_code: reservation.confirmation_code,
      last_name: form.last_name
    });
    setReservations((list) => list.filter((r) => r.id !== reservation.id));
  };

  const handleCancel = async (reservation) => {
    if (!window.confirm(`Cancel your reservation for seat ${reservation.inventory_label}?`)) return;
    try {
      await cancel(reservation);
      toast.success('Reservation cancelled');
    } catch (error) {
      toast.error('Could not cancel', { description: error.response?.data?.detail || 'Please try again' });
    }
  };

  const handleChange = async (reservation) => {
    if (!window.confirm(`To change, seat ${reservation.inventory_label} will be released and you can pick a new one. Continue?`)) return;
    try {
      await cancel(reservation);
      toast.success('Seat released', { description: 'Please select a new seat.' });
      navigate(getGuestSession() ? '/book' : '/check-eligibility');
    } catch (error) {
      toast.error('Could not change', { description: error.response?.data?.detail || 'Please try again' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="manage-page">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#78716C] hover:text-[#9F1239] mb-8" data-testid="manage-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>
        <h1 className="text-4xl sm:text-5xl font-serif text-[#1C1917] mb-3">Manage my reservation</h1>
        <p className="text-[#78716C] mb-10">View, change, or cancel your upcoming seat bookings.</p>

        <Card className="bg-white border-[#E7E5E4] mb-10">
          <CardContent className="p-6">
            <Tabs value={mode} onValueChange={setMode} className="mb-6">
              <TabsList>
                <TabsTrigger value="code" data-testid="manage-tab-code">Confirmation code</TabsTrigger>
                <TabsTrigger value="room" data-testid="manage-tab-room">Room / Unit number</TabsTrigger>
              </TabsList>
            </Tabs>
            <form onSubmit={lookup} className="grid sm:grid-cols-2 gap-4">
              {mode === 'code' ? (
                <div>
                  <Label htmlFor="manage-code">Confirmation code</Label>
                  <Input id="manage-code" value={form.confirmation_code} onChange={set('confirmation_code')} placeholder="e.g. 4CC83206" required data-testid="manage-input-code" />
                </div>
              ) : (
                <div>
                  <Label htmlFor="manage-room">Room / Unit number</Label>
                  <Input id="manage-room" value={form.room_number} onChange={set('room_number')} placeholder="e.g. 1203" required data-testid="manage-input-room" />
                </div>
              )}
              <div>
                <Label htmlFor="manage-last">Last name</Label>
                <Input id="manage-last" value={form.last_name} onChange={set('last_name')} required data-testid="manage-input-last-name" />
              </div>
              <Button type="submit" disabled={loading} className="sm:col-span-2 bg-[#9F1239] hover:bg-[#881337] text-white rounded-none" data-testid="manage-find-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Find my reservations
              </Button>
            </form>
          </CardContent>
        </Card>

        {reservations && (
          <div className="space-y-4" data-testid="manage-results">
            {reservations.length === 0 ? (
              <p className="text-[#78716C]" data-testid="manage-no-results">No upcoming active reservations found.</p>
            ) : (
              reservations.map((r) => (
                <ManageReservationCard key={r.id} reservation={r} onChange={handleChange} onCancel={handleCancel} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
